import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, BookOpen as BookOpenIcon, ExternalLink, Lock, RotateCcw, Loader2, CheckCircle2, ShieldCheck, AlertTriangle, Sun, Moon, Bookmark, List, PlayCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { useReadingTracker } from '../hooks/useReadingTracker';
import axios from 'axios';
import FlipbookReader from './FlipbookReader';
import { BookmarksPanel, AddBookmarkDialog } from './BookmarksPanel';

// Use current origin for deployed environment
const getAssetBaseUrl = () => window.location.origin;
const API = `${getAssetBaseUrl()}/api`;

const COMPLETION_THRESHOLD = 95;

// Inline keyframes for auto-resume fade
const fadeOutStyle = document.createElement('style');
if (!document.getElementById('book-reader-animations')) {
  fadeOutStyle.id = 'book-reader-animations';
  fadeOutStyle.textContent = `@keyframes fadeOut { from { opacity: 1; } to { opacity: 0; visibility: hidden; } }`;
  document.head.appendChild(fadeOutStyle);
}

const BookReader = ({ bookUrl, audiobookUrl, onClose, bookId, totalPages = 100 }) => {
  const [error, setError] = useState(false);
  const [errorDetail, setErrorDetail] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [savedPosition, setSavedPosition] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [readingMode, setReadingMode] = useState(() => localStorage.getItem('readingMode') || 'dark');
  const { toast } = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [resumedPage, setResumedPage] = useState(null);
  const progressSentRef = useRef(false);
  const positionSaveTimerRef = useRef(null);
  const lastSavedPositionRef = useRef({ page: 1, scroll: 0 });
  const iframeRef = useRef(null);
  const token = localStorage.getItem('token');
  const baseUrl = getAssetBaseUrl();

  // AI Verified Progress state
  const [verifiedProgress, setVerifiedProgress] = useState(null);
  const canComplete = verifiedProgress?.can_complete || false;
  const verifiedPct = verifiedProgress?.verified_percentage || 0;

  // Resume behavior (option 4b): we DO NOT auto-jump. We keep the
  // saved page hidden in `resumedPage` and let the user click a "Resume
  // from page N" overlay to actually jump.
  const [pendingResumePage, setPendingResumePage] = useState(null);
  const [totalPagesFromPdf, setTotalPagesFromPdf] = useState(null);
  // Bookmarks UI state
  const [bookmarkDialogOpen, setBookmarkDialogOpen] = useState(false);
  const [bookmarksPanelOpen, setBookmarksPanelOpen] = useState(false);
  const [bookmarksRefreshKey, setBookmarksRefreshKey] = useState(0);
  // Used to imperatively jump the PdfReader to a specific page (Resume / Bookmark)
  const [readerInitialPage, setReaderInitialPage] = useState(1);

  // Behavioral Reading Tracker (AI Intelligence) with progress callback
  const { setPageInfo, consumeViewCredit, flushEvents, fetchVerifiedProgress } = useReadingTracker({
    bookId,
    token,
    enabled: !!bookId && !!token,
    onProgressUpdate: (data) => setVerifiedProgress(data)
  });

  // Consume view credit on book open
  useEffect(() => {
    if (bookId && token) {
      consumeViewCredit();
    }
  }, [bookId, token, consumeViewCredit]);

  // Update tracker when page changes
  useEffect(() => {
    if (currentPage && totalPages) {
      setPageInfo(currentPage, totalPages);
    }
  }, [currentPage, totalPages, setPageInfo]);
  
  // Add token to URL as query parameter with cache-busting
  const fullUrl = `${baseUrl}${bookUrl}${bookUrl.includes('?') ? '&' : '?'}token=${token}`;
  const audioUrl = audiobookUrl ? `${baseUrl}${audiobookUrl}?token=${token}` : null;

  // Pre-rendered page images manifest — when the server has converted
  // this book's pages to WebP, the reader uses tiny per-page images
  // instead of decoding the PDF in the browser (fast on weak
  // connections, no pdf.js in the reading path). `null` = still
  // checking, `false` = not available → fall back to the PDF reader.
  const [pagesManifest, setPagesManifest] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!bookId) { setPagesManifest(false); return; }
      try {
        const res = await axios.get(`${API}/books/${bookId}/pages-manifest`);
        if (cancelled) return;
        if (res.data?.status === 'ready' && res.data?.count > 0) {
          setPagesManifest(res.data);
        } else {
          setPagesManifest(false);
        }
      } catch {
        if (!cancelled) setPagesManifest(false);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [bookId]);

  const imagePages = pagesManifest
    ? {
        count: pagesManifest.count,
        aspect: pagesManifest.aspect,
        urlFor: (pn) => `${baseUrl}/api/books/${bookId}/pages/${pn}?token=${token}`,
      }
    : null;

  // Simulate loading progress for better UX while the flipbook is fetching
  // its first page. Applies to mobile and desktop equally now that mobile
  // uses the same FlipbookReader (previous mobile static-screen branch
  // deleted 2026-07-26).
  useEffect(() => {
    if (loading) {
      const interval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 15;
        });
      }, 200);
      return () => clearInterval(interval);
    }
  }, [loading]);

  // Check if book is already completed
  useEffect(() => {
    const checkCompletionStatus = async () => {
      if (!bookId || !token) return;
      
      try {
        const response = await axios.get(
          `${API}/library/history`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const bookProgress = response.data.find(h => h.book_id === bookId);
        if (bookProgress?.status === 'completed') {
          setIsCompleted(true);
        }
      } catch (err) {
        console.log('Failed to check completion status');
      }
    };
    
    checkCompletionStatus();
  }, [bookId, token]);

  // Fetch saved reading position on mount and AUTO-RESUME
  useEffect(() => {
    const fetchSavedPosition = async () => {
      if (!bookId || !token) return;
      
      try {
        const response = await axios.get(
          `${API}/library/position/${bookId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data?.has_saved_position && response.data.page_number > 1) {
          setSavedPosition(response.data);
          // Option 4b: don't auto-jump. Stage the saved page so the
          // user can click a "Resume from page N" overlay button.
          setPendingResumePage(response.data.page_number);
          lastSavedPositionRef.current = {
            page: 1, // start at 1; updated when user clicks Resume or navigates
            scroll: response.data.scroll_position || 0,
          };
        }
      } catch (err) {
        console.log('No saved position found:', err);
      }
    };
    
    fetchSavedPosition();
  }, [bookId, token]);

  // Open Mark-As-Complete logic moved up so it's defined before render
  const markAsComplete = useCallback(async () => {
    if (!bookId || !token) return;
    
    // Flush events and check progress before allowing completion
    await flushEvents();
    const progress = await fetchVerifiedProgress();
    
    if (progress && !progress.can_complete) {
      toast({
        title: 'Cannot mark as complete',
        description: `You need ${COMPLETION_THRESHOLD}% verified reading. Currently at ${progress.verified_percentage}%.`,
        variant: 'destructive',
      });
      return;
    }
    
    try {
      await axios.post(
        `${API}/books/${bookId}/progress`,
        { progress_percentage: 100 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setIsCompleted(true);
      toast({
        title: 'Book Completed!',
        description: 'Great job! Your verified reading has been confirmed.',
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to mark book as complete',
        variant: 'destructive',
      });
    }
  }, [bookId, token, toast, flushEvents, fetchVerifiedProgress]);

  // Save reading position immediately on page change
  const saveReadingPosition = useCallback(async (pageNumber, scrollPosition = 0) => {
    if (!bookId || !token) return;
    
    // Don't save if position hasn't changed
    if (pageNumber === lastSavedPositionRef.current.page && 
        Math.abs(scrollPosition - lastSavedPositionRef.current.scroll) < 5) {
      return;
    }
    
    lastSavedPositionRef.current = { page: pageNumber, scroll: scrollPosition };
    setCurrentPage(pageNumber);
    
    try {
      await axios.post(
        `${API}/library/position/${bookId}`,
        { 
          page_number: pageNumber, 
          scroll_position: scrollPosition,
          total_pages: totalPages
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Also update reading progress percentage in real-time
      const percentage = Math.max(1, Math.min(100, Math.round((pageNumber / totalPages) * 100)));
      await axios.post(
        `${API}/books/${bookId}/progress`,
        { progress_percentage: percentage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.log('Failed to save position:', err);
    }
  }, [bookId, token, totalPages]);

  // ── PdfReader callbacks (must come AFTER saveReadingPosition so the
  //     useCallback closure picks up the resolved reference). ──
  const handlePdfPageChange = useCallback(
    (newPage, totalFromPdf) => {
      setCurrentPage(newPage);
      if (totalFromPdf && totalFromPdf !== totalPagesFromPdf) {
        setTotalPagesFromPdf(totalFromPdf);
      }
      saveReadingPosition(newPage, 0);
    },
    [saveReadingPosition, totalPagesFromPdf],
  );

  const handlePdfLoadSuccess = useCallback(({ numPages }) => {
    setLoading(false);
    setLoadingProgress(100);
    setTotalPagesFromPdf(numPages);
  }, []);

  const handlePdfLoadError = useCallback(async (err) => {
    // Log the full error to the console FIRST so future issues can be
    // diagnosed from DevTools / crash reports. pdf.js errors have a
    // `.name` (e.g. UnexpectedResponseException, InvalidPDFException,
    // MissingPDFException) and a `.message` — both are useful.
    console.error('[BookReader] pdf.js load error', {
      name: err?.name,
      message: err?.message,
      status: err?.status,
      stack: err?.stack,
      raw: err,
    });

    // Distinguish between a session-expired 401 and a generic server /
    // file failure. On session expiry we don't want to show "The server
    // may be temporarily unavailable" — the server is fine, the user is
    // just no longer authenticated. Route them to the login page with a
    // ?next=... hint so they land back in the same book after re-auth.
    // On any other failure fall through to the normal error UI so the
    // reader can Retry / Open in New Tab as before.
    setLoading(false);

    // pdf.js surfaces HTTP failures as UnexpectedResponseException with
    // a numeric `status` field. Older builds only carry the status in
    // the human-readable message string, so we probe both.
    const name = err?.name || '';
    const status = err?.status;
    const message = (err?.message || err?.toString?.() || '').toLowerCase();
    let is401 =
      status === 401 ||
      (name === 'UnexpectedResponseException' && /401|unauthor/i.test(message)) ||
      /\b401\b|unauthor|token/i.test(message);

    // If the shape doesn't clearly signal 401, do one cheap authed probe
    // (goes through our axios instance, which will trigger the global
    // session_invalidated handler if the admin force-logged-out — so
    // this doubles as a redirect for that case). We only probe when we
    // still have a token — otherwise we already know we're logged out.
    if (!is401) {
      const t = localStorage.getItem('token');
      if (!t) {
        is401 = true;
      } else if (bookId) {
        try {
          await axios.get(`${API}/library/position/${bookId}`, {
            headers: { Authorization: `Bearer ${t}` },
          });
        } catch (probeErr) {
          if (probeErr?.response?.status === 401) {
            is401 = true;
          }
        }
      }
    }

    if (is401) {
      // Wipe local session artefacts, then hard-navigate to /login with
      // a `next` querystring so the reader lands back at this exact book
      // after signing in again. Uses window.location.replace so the
      // /book/... URL isn't left in browser history.
      try { localStorage.removeItem('token'); } catch { /* no-op */ }
      try { localStorage.removeItem('user'); } catch { /* no-op */ }
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.replace(`/login?next=${next}&bust=${Date.now()}`);
      return;
    }

    // Surface a short technical detail on the error card so the user
    // (and support) can distinguish network / worker / PDF-parse issues
    // at a glance. Keeps the friendly headline but adds an inline hint.
    const brief =
      err?.name && err?.message
        ? `${err.name}: ${err.message}`
        : (err?.message || err?.toString?.() || 'Unknown error');
    // Trim to a single line, cap length so the UI stays clean.
    setErrorDetail(brief.replace(/\s+/g, ' ').slice(0, 240));
    setError(true);
  }, [bookId]);

  // User clicks the "Resume from page N" overlay
  const acceptResume = useCallback(() => {
    if (!pendingResumePage) return;
    setReaderInitialPage(pendingResumePage);
    setCurrentPage(pendingResumePage);
    setResumedPage(pendingResumePage);
    setPendingResumePage(null);
    toast({
      title: 'Resumed',
      description: `Jumped to page ${pendingResumePage}`,
    });
  }, [pendingResumePage, toast]);

  const dismissResume = useCallback(() => setPendingResumePage(null), []);

  // Auto-save position every 15 seconds while reading
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isMobile && bookId) {
        const page = lastSavedPositionRef.current.page;
        saveReadingPosition(page, 0);
      }
    }, 15000);
    
    // Save position on unmount (user closes reader)
    return () => {
      clearInterval(interval);
      if (positionSaveTimerRef.current) {
        clearTimeout(positionSaveTimerRef.current);
      }
      // Final save on close using sendBeacon for reliability
      const { page, scroll } = lastSavedPositionRef.current;
      if (page > 1 && bookId && token) {
        const data = JSON.stringify({ page_number: page, scroll_position: scroll, total_pages: totalPages });
        if (navigator.sendBeacon) {
          navigator.sendBeacon(
            `${API}/library/position-beacon/${bookId}?token=${token}`,
            new Blob([data], { type: 'application/json' })
          );
        }
      }
    };
  }, [isMobile, bookId, saveReadingPosition, token, totalPages]);

  // Track reading progress
  const trackProgress = useCallback(async (percentage) => {
    if (!bookId || !token) return;
    
    try {
      await axios.post(
        `${API}/books/${bookId}/progress`,
        { progress_percentage: percentage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.log('Failed to track progress:', err);
    }
  }, [bookId, token]);

  // Track initial read when component mounts - use actual page-based percentage
  useEffect(() => {
    if (!progressSentRef.current && bookId) {
      progressSentRef.current = true;
      // Calculate real percentage from current page (starts at page 1)
      const initialPage = resumedPage || 1;
      const percentage = Math.max(1, Math.min(100, Math.round((initialPage / totalPages) * 100)));
      trackProgress(percentage);
    }
  }, [bookId, trackProgress, resumedPage, totalPages]);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 || 
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Open PDF in new tab (best for mobile) - track progress
  const openInNewTab = () => {
    const page = lastSavedPositionRef.current.page || 1;
    const percentage = Math.max(1, Math.min(100, Math.round((page / totalPages) * 100)));
    trackProgress(percentage);
    window.open(fullUrl, '_blank');
  };

  // Timeout for iframe loading - if it takes too long, show error with retry option
  useEffect(() => {
    if (loading && !error && !isMobile) {
      const timeout = setTimeout(() => {
        if (loading) {
          console.log('PDF loading timeout - showing retry option');
          setError(true);
          setLoading(false);
        }
      }, 30000); // 30 second timeout

      return () => clearTimeout(timeout);
    }
  }, [loading, error, isMobile]);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-700 p-3 sm:p-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center space-x-2 sm:space-x-4">
          <Button variant="ghost" size="icon" onClick={onClose} className="text-white h-8 w-8 sm:h-10 sm:w-10">
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </Button>
          <div className="flex items-center space-x-2">
            <BookOpenIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            <span className="text-white font-semibold text-sm sm:text-base">Reading Book</span>
            {currentPage > 1 && (
              <span className="text-gray-400 text-xs sm:text-sm">Page {currentPage}</span>
            )}
          </div>
        </div>
        
        {/* Verified Progress + Actions */}
        <div className="flex items-center space-x-1 sm:space-x-3">
          {/* AI Verified Reading Progress */}
          {verifiedProgress && !isCompleted && (
            <div className="hidden sm:flex items-center gap-2 mr-2" data-testid="verified-progress">
              <ShieldCheck className={`h-4 w-4 ${canComplete ? 'text-green-400' : 'text-amber-400'}`} />
              <div className="flex flex-col">
                <div className="flex items-center gap-1.5">
                  <div className="w-20 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-700 ${canComplete ? 'bg-green-500' : 'bg-amber-500'}`}
                      style={{ width: `${Math.min(verifiedPct, 100)}%` }}
                    />
                  </div>
                  <span className={`text-[10px] font-bold ${canComplete ? 'text-green-400' : 'text-amber-400'}`}>
                    {verifiedPct}%
                  </span>
                </div>
                <span className="text-[9px] text-gray-500">
                  {verifiedProgress.verified_pages}/{verifiedProgress.total_pages} pages verified
                </span>
              </div>
            </div>
          )}

          {/* Bookmark current page */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setBookmarkDialogOpen(true)}
            className="text-amber-300 hover:bg-amber-500/10 text-xs sm:text-sm px-2 sm:px-3"
            title={`Bookmark page ${currentPage}`}
            data-testid="reader-add-bookmark"
          >
            <Bookmark className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Bookmark</span>
          </Button>
          {/* Open bookmarks panel */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setBookmarksPanelOpen(true)}
            className="text-white hover:bg-white/10 text-xs sm:text-sm px-2 sm:px-3"
            title="Your bookmarks for this book"
            data-testid="reader-open-bookmarks"
          >
            <List className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Bookmarks</span>
          </Button>

          {/* Mark as Complete Button - conditionally enabled */}
          {!isCompleted ? (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={markAsComplete}
              disabled={!canComplete}
              className={`text-xs sm:text-sm px-2 sm:px-3 cursor-pointer ${
                canComplete 
                  ? 'text-green-400 hover:bg-green-500/10' 
                  : 'text-gray-500 cursor-not-allowed opacity-60'
              }`}
              title={canComplete ? 'Mark as Completed' : `${COMPLETION_THRESHOLD}% verified reading required (${verifiedPct}% done)`}
              data-testid="mark-complete-btn"
            >
              {canComplete ? (
                <CheckCircle2 className="h-4 w-4 sm:mr-2" />
              ) : (
                <AlertTriangle className="h-4 w-4 sm:mr-2" />
              )}
              <span className="hidden sm:inline">
                {canComplete ? 'Mark as Completed' : `Complete (${COMPLETION_THRESHOLD}% req)`}
              </span>
            </Button>
          ) : (
            <div className="flex items-center space-x-1 text-green-400 text-xs px-2" data-testid="completed-badge">
              <CheckCircle2 className="h-4 w-4" />
              <span className="hidden sm:inline">Completed</span>
            </div>
          )}
          <Button 
            variant="ghost" 
            size="sm"
            onClick={openInNewTab}
            className="text-white hover:bg-white/10 text-xs sm:text-sm px-2 sm:px-3"
            title="Open in new tab for full screen reading"
          >
            <ExternalLink className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Full Screen</span>
          </Button>
          {/* Dark/Light Reading Mode Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const newMode = readingMode === 'dark' ? 'light' : readingMode === 'light' ? 'sepia' : 'dark';
              setReadingMode(newMode);
              localStorage.setItem('readingMode', newMode);
            }}
            className={`text-xs sm:text-sm px-2 sm:px-3 ${
              readingMode === 'light' ? 'text-yellow-400 hover:bg-yellow-500/10' :
              readingMode === 'sepia' ? 'text-amber-400 hover:bg-amber-500/10' :
              'text-blue-400 hover:bg-blue-500/10'
            }`}
            title={`Reading mode: ${readingMode} (click to switch)`}
            data-testid="reading-mode-toggle"
          >
            {readingMode === 'dark' ? <Moon className="h-4 w-4 sm:mr-2" /> : <Sun className="h-4 w-4 sm:mr-2" />}
            <span className="hidden sm:inline capitalize">{readingMode}</span>
          </Button>
          {/* Protected content notice */}
          <div className="hidden sm:flex items-center space-x-1 text-gray-400 text-xs ml-2">
            <Lock className="h-3 w-3" />
            <span>Protected</span>
          </div>
        </div>
      </div>

      {/* PDF Viewer */}
      <div className={`flex-1 overflow-auto relative ${
        readingMode === 'light' ? 'bg-white' : readingMode === 'sepia' ? 'bg-amber-50' : 'bg-gray-800'
      }`} style={{ minHeight: 0 }}>
        {/* Auto-resume indicator (briefly shown) */}
        {resumedPage && resumedPage > 1 && !loading && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20 bg-green-900/90 border border-green-600/50 rounded-lg px-4 py-2 shadow-xl animate-in fade-in slide-in-from-top-2"
            style={{ animation: 'fadeOut 3s forwards 2s' }}
          >
            <p className="text-green-300 text-sm font-medium">
              Resumed from page {resumedPage}
            </p>
          </div>
        )}

        {loading && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-800 z-10">
            <div className="text-center text-white max-w-xs">
              <Loader2 className="h-12 w-12 animate-spin text-red-500 mx-auto mb-4" />
              <p className="font-medium mb-2">Loading book...</p>
              {/* Progress bar */}
              <div className="w-48 h-2 bg-gray-700 rounded-full mx-auto overflow-hidden">
                <div 
                  className="h-full bg-red-500 rounded-full transition-all duration-300"
                  style={{ width: `${loadingProgress}%` }}
                />
              </div>
              <p className="text-gray-400 text-sm mt-2">{Math.round(loadingProgress)}%</p>
            </div>
          </div>
        )}
        
        {error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-white p-4">
              <p className="text-xl mb-4">Unable to load book</p>
              <p className="text-gray-400 mb-6">
                {retryCount >= 2 
                  ? "The book may be too large to display inline. Try opening in a new tab."
                  : "The server may be temporarily unavailable. Please try again."
                }
              </p>
              {errorDetail && (
                <details
                  className="mb-6 text-left max-w-md mx-auto"
                  data-testid="reader-error-detail"
                >
                  <summary className="text-gray-500 text-xs cursor-pointer hover:text-gray-300 select-none">
                    Technical details
                  </summary>
                  <pre className="mt-2 text-[11px] text-gray-500 bg-white/5 border border-white/10 rounded-md p-2 whitespace-pre-wrap break-words">
                    {errorDetail}
                  </pre>
                </details>
              )}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {retryCount < 3 && (
                  <Button 
                    onClick={() => {
                      setError(false);
                      setErrorDetail(null);
                      setLoading(true);
                      setLoadingProgress(0);
                      setRetryCount(prev => prev + 1);
                    }} 
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Retry ({3 - retryCount} left)
                  </Button>
                )}
                <Button onClick={openInNewTab} className="bg-red-600 hover:bg-red-700">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open in New Tab
                </Button>
              </div>
              <p className="text-gray-500 text-sm mt-4 flex items-center justify-center">
                <Lock className="h-3 w-3 mr-1" />
                Content is protected and cannot be downloaded
              </p>
            </div>
          </div>
        ) : pagesManifest === null ? (
          /* Waiting on the pages-manifest check (fast, single request) */
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          /* 3D Flipbook reader — canvas-only anti-copy, 2-page spread on desktop, 1-page on mobile */
          <FlipbookReader
            src={fullUrl}
            imagePages={imagePages}
            initialPage={readerInitialPage}
            onPageChange={handlePdfPageChange}
            onLoadSuccess={handlePdfLoadSuccess}
            onLoadError={handlePdfLoadError}
            readingMode={readingMode}
            watermarkText="Seven Chapter Books"
            dataTestId="reader-flipbook"
          />
        )}

        {/* "Resume from page N" overlay (option 4b — user click, no auto-jump) */}
        {pendingResumePage && pendingResumePage > 1 && !loading && !error && (
          <div
            className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20 bg-gray-900/95 border border-amber-500/40 rounded-lg px-4 py-3 shadow-2xl flex items-center gap-3"
            data-testid="resume-overlay"
          >
            <PlayCircle className="h-5 w-5 text-amber-300 flex-shrink-0" />
            <div className="text-sm">
              <p className="text-white font-medium">Pick up where you left off?</p>
              <p className="text-gray-400 text-xs">
                Saved on page {pendingResumePage}
                {savedPosition?.total_pages ? ` of ${savedPosition.total_pages}` : ''}
              </p>
            </div>
            <Button
              size="sm"
              onClick={acceptResume}
              className="h-8 bg-amber-500 hover:bg-amber-600 text-amber-950 font-semibold"
              data-testid="resume-accept"
            >
              Resume
            </Button>
            <button
              type="button"
              onClick={dismissResume}
              className="p-1 text-gray-400 hover:text-white"
              data-testid="resume-dismiss"
              title="Start from the beginning"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Bookmarks side panel */}
      <BookmarksPanel
        bookId={bookId}
        open={bookmarksPanelOpen}
        onClose={() => setBookmarksPanelOpen(false)}
        onJumpToPage={(p) => {
          setReaderInitialPage(p);
          setCurrentPage(p);
          setBookmarksPanelOpen(false);
        }}
        refreshKey={bookmarksRefreshKey}
      />

      {/* Add bookmark dialog */}
      <AddBookmarkDialog
        open={bookmarkDialogOpen}
        currentPage={currentPage}
        bookId={bookId}
        onClose={() => setBookmarkDialogOpen(false)}
        onCreated={() => setBookmarksRefreshKey((k) => k + 1)}
      />
    </div>
  );
};

export default BookReader;
